/* PR 45 review · D2 (Card V3) — the engagement's country, currency and weekend on the screens.
 *
 * COUNTRY_CODES mirrors backend/engagement_locale.py (a backend test compares the two lists). The EN / AR names come from
 * the browser's own country / currency names (Intl.DisplayNames, CLDR data) so no name is typed by hand here.
 * CURRENCY_CODES: the ISO 4217 codes the ICU 77.1 / CLDR 47 library lists (Node 22 Intl.supportedValuesOf("currency"),
 * 162 codes); a currency not in the list is typed under "Other" (1–10 characters, no < or >, kept exactly as typed).
 */

export const COUNTRY_CODES = (
  "AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF " +
  "CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH " +
  "GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW " +
  "KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG " +
  "NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN " +
  "SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS XK YE " +
  "YT ZA ZM ZW"
).split(" ");

export const CURRENCY_CODES = (
  "AED AFN ALL AMD ANG AOA ARS AUD AWG AZN BAM BBD BDT BGN BHD BIF BMD BND BOB BRL BSD BTN BWP BYN BZD CAD CDF CHF CLP CNY " +
  "COP CRC CUC CUP CVE CZK DJF DKK DOP DZD EGP ERN ETB EUR FJD FKP GBP GEL GHS GIP GMD GNF GTQ GYD HKD HNL HRK HTG HUF IDR " +
  "ILS INR IQD IRR ISK JMD JOD JPY KES KGS KHR KMF KPW KRW KWD KYD KZT LAK LBP LKR LRD LSL LYD MAD MDL MGA MKD MMK MNT MOP " +
  "MRU MUR MVR MWK MXN MYR MZN NAD NGN NIO NOK NPR NZD OMR PAB PEN PGK PHP PKR PLN PYG QAR RON RSD RUB RWF SAR SBD SCR SDG " +
  "SEK SGD SHP SLE SLL SOS SRD SSP STN SVC SYP SZL THB TJS TMT TND TOP TRY TTD TWD TZS UAH UGX USD UYU UZS VES VND VUV WST " +
  "XAF XCD XCG XDR XOF XPF XSU YER ZAR ZMW ZWG ZWL"
).split(" ");

export const CURRENCY_MAX_LEN = 10;
export const OTHER = "__other__";

// Mirrors engagement_settings.FRI_SAT_COUNTRIES (codes only): the country's suggested weekend — a suggestion the user can untick
const FRI_SAT = new Set(["SA", "KW", "QA", "OM", "BH", "EG", "JO", "IQ", "SY", "YE", "LY", "SD"]);
export const suggestedWeekend = (country) => (country ? (FRI_SAT.has(String(country).toUpperCase()) ? [4, 5] : [5, 6]) : null);

const _names = {};
const _dn = (lang, type) => {
  const k = `${lang}:${type}`;
  if (!(k in _names)) {
    try { _names[k] = new Intl.DisplayNames([lang === "ar" ? "ar" : "en"], { type, fallback: "code" }); } catch { _names[k] = null; }
  }
  return _names[k];
};
export const countryName = (code, lang = "en") => {
  if (!code) return "";
  try { return _dn(lang, "region")?.of(String(code).toUpperCase()) || code; } catch { return code; }
};
export const currencyName = (code, lang = "en") => {
  if (!code) return "";
  try { return _dn(lang, "currency")?.of(String(code).toUpperCase()) || code; } catch { return code; }
};

export const isListedCurrency = (v) => CURRENCY_CODES.includes(String(v || ""));

/** The "Other" text: trimmed, 1–10 characters, no < or >. Returns "" when valid, else the reason key. */
export function otherCurrencyError(text) {
  const v = String(text ?? "").trim();
  if (!v) return "required";
  if (v.length > CURRENCY_MAX_LEN || /[<>]/.test(v)) return "invalid";
  return "";
}

/** The stored currency text exactly as stored (trimmed); "" when none — never a guessed "AED". */
export const currencyText = (eng) => String(eng?.currency ?? "").trim();

export const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];
export const sameDays = (a, b) => JSON.stringify([...(a || [])].sort()) === JSON.stringify([...(b || [])].sort());
